/**
 * Argon Dashboard 2 PRO MUI - v3.0.1
 * Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)
 */

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components

// Argon Dashboard components
import { useSession } from "0.Mine/2.Context/sessionContext";
import AdminRoutes from "AdminRoutes";
import brandDark from "assets/images/logo-ct-dark.png";
import brand from "assets/images/logo-ct.png";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import ArgonBox from "components/ArgonBox";
import { useArgonController, setMiniSidenav } from "context";
import Sidenav from "examples/Sidenav";

// Themes

// Routes
import routes from "routes";

// Context

// Assets

// Icon Fonts
import "assets/css/nucleo-icons.css";
import "assets/css/nucleo-svg.css";

export default function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session } = useSession();
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, layout, sidenavColor, darkSidenav, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [sessionRoutes, setSessionRoutes] = useState(routes);

  // Handle onboarding redirect
  useEffect(() => {
    if (session?.user.onboarding === false) {
      navigate("/onboarding");
    }
  }, [session]);

  // Open sidenav when mouse enters mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leaves mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Set up routes based on user permissions
  useEffect(() => {
    // Reset scroll position when changing routes
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    // Set up routes based on user role and permissions
    if (session) {
      let newRoutes = [];

      if (session.user.qlickup === "vendor") {
        const permissions = session.user.permissions;

        if (permissions) {
          routes.forEach((route) => {
            // Include main routes with permission
            if (permissions[route.key] === true) {
              newRoutes.push(route);
            }
            // Handle collapsible routes with sub-permissions
            else if (typeof permissions[route.key] === "object" && route.collapse) {
              const filteredRoute = { ...route };
              filteredRoute.collapse = route.collapse.filter(
                (item) => permissions[route.key][item.key] === true
              );

              if (filteredRoute.collapse.length > 0) {
                newRoutes.push(filteredRoute);
              }
            }
          });
        }
      } else if (session.user.qlickup === "user" || session.user.qlickup === "admin") {
        newRoutes = [...routes, ...AdminRoutes];
      }

      setSessionRoutes(newRoutes);
    }
  }, [session, pathname]);

  // Generate routes from route configuration
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={darkSidenav || darkMode ? brand : brandDark}
          brandName=""
          routes={sessionRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Routes>
        {getRoutes(sessionRoutes)}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </ThemeProvider>
  );
}
